import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import CalendarIcon from "../img/calendaricon.svg";
import { instance2 } from "../utils/axiosInstance"; // adjust your axios import if needed
import { registerLocale } from "react-datepicker";
import id from "date-fns/locale/id"; // Indonesian locale
import { basecode } from "../mainscreen/pesananscreen/baserobotocode";
import BgnIcon from "../img/logobgn250.png";
import { formatNumber } from "../numberingformat";
import { ReactComponent as BackButton } from "../img/bacbuttonicon.svg";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../shadcn/select";
import { Button } from "../shadcn/button";
import { basecodeBold } from "./pesananscreen/baserobotoboldcode";
registerLocale("id", id);

export default function DailyReport() {
  const roleId = parseInt(localStorage.getItem("roleId"), 10);
  const kitchenId = parseInt(localStorage.getItem("kitchenId"), 10);
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  let pageNumbers = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  } else {
    if (currentPage <= 3) {
      pageNumbers = [1, 2, 3, 4, 5, "...", totalPages];
    } else if (currentPage >= totalPages - 2) {
      pageNumbers = [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    } else {
      pageNumbers = [
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages,
      ];
    }
  }

  const CustomInput = React.forwardRef(({ value, onClick, isOpen }, ref) => (
    <button
      className="flex items-center justify-between w-full px-4 py-3 border rounded-full bg-white text-xxs md:text-xxs lg:text-base"
      onClick={onClick}
      ref={ref}
      type="button"
    >
      <div className="flex items-center gap-2">
        <img src={CalendarIcon} alt="Calendar" className="h-5 w-5" />
        <div className="w-px h-5 bg-gray-300 mx-1"></div>
        <span>
          {new Date(value).toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
        </span>
      </div>
      <div
        className={`text-black font-semibold transform transition-transform duration-500 ease-in-out ${
          isOpen ? "rotate-180" : "rotate-0"
        }`}
      >
        ▼
      </div>
    </button>
  ));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await instance2.get(
          `customers/result/${kitchenId}/${format(selectedDate, "yyyy-MM-dd")}`,
        );

        const responseData = response.data || [];
        const finalData = responseData.map((item) => ({
          customer_id: item.id,
          customer_name: item.customer_name,
          date: item.date,
          pagi: {
            qty_sch: item.qty_sch_pagi || 0,
            qty_real: item.qty_real_pagi || 0,
            received_qty: item.received_qty_pagi || 0,
          },
          siang: {
            qty_sch: item.qty_sch_siang || 0,
            qty_real: item.qty_real_siang || 0,
            received_qty: item.received_qty_siang || 0,
          },
          rate_qty: (item.rate_qty_pagi + item.rate_qty_siang) / 2,
          rate_quality: (item.rate_quality_pagi + item.rate_quality_siang) / 2,
        }));
        setData(finalData);
      } catch (error) {
        console.error("Error fetching production data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, kitchenId]);

  const downloadPDF = async () => {
    if (data.length === 0) {
      alert("Tidak ada data untuk diunduh.");
      return;
    }
    const doc = new jsPDF({ orientation: "landscape" });
    const kitchenName = localStorage.getItem("kitchenname") || "Dapur";
    const LEFT_MARGIN = 30;
    const RIGHT_MARGIN = 20;

    // Embed Roboto font
    doc.addFileToVFS("Roboto-Regular.ttf", basecode);
    doc.addFileToVFS("Roboto-Bold.ttf", basecodeBold);
    doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
    doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");
    doc.setFont("Roboto", "normal");

    // Load icon as base64
    const loadImageAsBase64 = (imgSrc) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = imgSrc;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = (err) => reject(err);
      });

    const iconBase64 = await loadImageAsBase64(BgnIcon);
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Header function
    const addHeader = (doc, pageWidth) => {
      const textYOffset = 10;

      // Icon on the LEFT
      const iconWidth = 23;
      const iconHeight = 23;
      const iconX = LEFT_MARGIN;
      const iconY = textYOffset + 2;
      doc.addImage(iconBase64, "PNG", iconX, iconY, iconWidth, iconHeight);

      doc.setFontSize(12);
      doc.setFont("Roboto", "bold");
      doc.setTextColor(59, 78, 135);
      doc.text(
        "LAPORAN PRODUKSI HARIAN",
        LEFT_MARGIN,
        textYOffset + 19 + iconHeight,
        {
          align: "left",
        },
      );
      doc.setTextColor(0, 0, 0);

      const alamatLines = [
        "Kompleks Kementerian Pertanian",
        "Jl Harsono RM No.3 Gedung E",
        "Ragunan, Ps. Minggu, Kota Jakarta Selatan, Daerah Khusus Ibukota Jakarta 12550",
      ];
      const linkBgn = "https://www.bgn.go.id/";
      const textWidthLinkBgn = doc.getTextWidth(linkBgn);

      const startX = LEFT_MARGIN + iconWidth + 6;
      let startY = textYOffset + iconHeight / 2 - 4;

      // Loop through the lines and print them in a column
      alamatLines.forEach((line) => {
        doc.setFontSize(8);
        doc.text(line, startX, startY);
        startY += 4; // move down for the next line
      });

      // Draw link on top
      doc.setTextColor(0, 120, 255);
      doc.textWithLink(linkBgn, startX, startY, { url: linkBgn });

      // Underline
      doc.setDrawColor(0, 120, 255);
      doc.line(startX, startY + 1, startX + textWidthLinkBgn - 14, startY + 1);

      // Reset color
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(0, 0, 0);

      // Info section
      const margin = 14;
      const rowHeight = 8;
      const colWidth = (pageWidth - (LEFT_MARGIN + RIGHT_MARGIN + margin)) / 2; // Two columns
      const startYInfo = 60;
      const RECT_PADDING = 4;

      // Dapur column
      doc.setFillColor(59, 78, 135);
      doc.rect(LEFT_MARGIN, startYInfo, colWidth, rowHeight, "F");
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.setFont("Roboto", "bold");
      doc.text("DAPUR", LEFT_MARGIN + RECT_PADDING, startYInfo + 5.5);
      doc.setTextColor(0, 0, 0);
      doc.setFont("Roboto", "normal");
      doc.setFontSize(10);
      doc.text(
        kitchenName,
        LEFT_MARGIN + RECT_PADDING,
        startYInfo + rowHeight + 6,
      );

      // Tanggal column
      const secondColX = LEFT_MARGIN + colWidth + margin;
      doc.setFillColor(59, 78, 135);
      doc.rect(secondColX, startYInfo, colWidth, rowHeight, "F");
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.setFont("Roboto", "bold");
      doc.text("TANGGAL", secondColX + RECT_PADDING, startYInfo + 5.5);
      doc.setTextColor(0, 0, 0);
      doc.setFont("Roboto", "normal");
      doc.setFontSize(10);
      const dateValue = selectedDate.toLocaleDateString("id-ID", {
        year: "numeric",
        month: "long",
        day: "2-digit",
      });
      doc.text(
        dateValue,
        secondColX + RECT_PADDING,
        startYInfo + rowHeight + 6,
      );

      return startYInfo + rowHeight + 18;
    };

    let yOffset = addHeader(doc, pageWidth);

    const tableData = data.map((item, index) => {
      const totalSch = (item.pagi.qty_sch || 0) + (item.siang.qty_sch || 0);
      const totalReal = (item.pagi.qty_real || 0) + (item.siang.qty_real || 0);
      const totalReceived =
        (item.pagi.received_qty || 0) + (item.siang.received_qty || 0);
      return [
        index + 1,
        item.customer_name ?? "-",
        formatNumber(item.pagi.qty_sch) ?? "-",
        formatNumber(item.siang.qty_sch) ?? "-",
        formatNumber(totalSch),
        formatNumber(item.pagi.qty_real) ?? "-",
        formatNumber(item.siang.qty_real) ?? "-",
        formatNumber(totalReal),
        formatNumber(item.pagi.received_qty) ?? "-",
        formatNumber(item.siang.received_qty) ?? "-",
        formatNumber(totalReceived),
      ];
    });

    const totals = data.reduce(
      (acc, item) => {
        acc.pagi_qty_sch += item.pagi.qty_sch || 0;
        acc.siang_qty_sch += item.siang.qty_sch || 0;
        acc.total_qty_sch +=
          (item.pagi.qty_sch || 0) + (item.siang.qty_sch || 0);
        acc.pagi_qty_real += item.pagi.qty_real || 0;
        acc.siang_qty_real += item.siang.qty_real || 0;
        acc.total_qty_real +=
          (item.pagi.qty_real || 0) + (item.siang.qty_real || 0);
        acc.pagi_received_qty += item.pagi.received_qty || 0;
        acc.siang_received_qty += item.siang.received_qty || 0;
        acc.total_received_qty +=
          (item.pagi.received_qty || 0) + (item.siang.received_qty || 0);
        return acc;
      },
      {
        pagi_qty_sch: 0,
        siang_qty_sch: 0,
        total_qty_sch: 0,
        pagi_qty_real: 0,
        siang_qty_real: 0,
        total_qty_real: 0,
        pagi_received_qty: 0,
        siang_received_qty: 0,
        total_received_qty: 0,
      },
    );

    const footer = [
      [
        {
          content: "Total",
          colSpan: 2,
          styles: {
            halign: "center",
            fontStyle: "bold",
            fillColor: [59, 78, 135],
            textColor: [255, 255, 255],
          },
        },
        {
          content: formatNumber(totals.pagi_qty_sch),
          styles: {
            halign: "right",
            fontStyle: "bold",
            fillColor: [59, 78, 135],
            textColor: [255, 255, 255],
          },
        },
        {
          content: formatNumber(totals.siang_qty_sch),
          styles: {
            halign: "right",
            fontStyle: "bold",
            fillColor: [59, 78, 135],
            textColor: [255, 255, 255],
          },
        },
        {
          content: formatNumber(totals.total_qty_sch),
          styles: {
            halign: "right",
            fontStyle: "bold",
            fillColor: [59, 78, 135],
            textColor: [255, 255, 255],
          },
        },
        {
          content: formatNumber(totals.pagi_qty_real),
          styles: {
            halign: "right",
            fontStyle: "bold",
            fillColor: [59, 78, 135],
            textColor: [255, 255, 255],
          },
        },
        {
          content: formatNumber(totals.siang_qty_real),
          styles: {
            halign: "right",
            fontStyle: "bold",
            fillColor: [59, 78, 135],
            textColor: [255, 255, 255],
          },
        },
        {
          content: formatNumber(totals.total_qty_real),
          styles: {
            halign: "right",
            fontStyle: "bold",
            fillColor: [59, 78, 135],
            textColor: [255, 255, 255],
          },
        },
        {
          content: formatNumber(totals.pagi_received_qty),
          styles: {
            halign: "right",
            fontStyle: "bold",
            fillColor: [59, 78, 135],
            textColor: [255, 255, 255],
          },
        },
        {
          content: formatNumber(totals.siang_received_qty),
          styles: {
            halign: "right",
            fontStyle: "bold",
            fillColor: [59, 78, 135],
            textColor: [255, 255, 255],
          },
        },
        {
          content: formatNumber(totals.total_received_qty),
          styles: {
            halign: "right",
            fontStyle: "bold",
            fillColor: [59, 78, 135],
            textColor: [255, 255, 255],
          },
        },
      ],
    ];

    doc.autoTable({
      theme: "grid",
      head: [
        [
          {
            content: "No",
            rowSpan: 2,
            styles: { valign: "middle", halign: "center" },
          },
          {
            content: "Penerima Manfaat",
            rowSpan: 2,
            styles: { valign: "middle", halign: "center" },
          },
          { content: "Rencana", colSpan: 3, styles: { halign: "center" } },
          { content: "Pengiriman", colSpan: 3, styles: { halign: "center" } },
          { content: "Diterima", colSpan: 3, styles: { halign: "center" } },
        ],
        [
          { content: "Pagi", styles: { halign: "center" } },
          { content: "Siang", styles: { halign: "center" } },
          { content: "Total", styles: { halign: "center" } },
          { content: "Pagi", styles: { halign: "center" } },
          { content: "Siang", styles: { halign: "center" } },
          { content: "Total", styles: { halign: "center" } },
          { content: "Pagi", styles: { halign: "center" } },
          { content: "Siang", styles: { halign: "center" } },
          { content: "Total", styles: { halign: "center" } },
        ],
      ],
      body: tableData,
      foot: footer,
      margin: { left: LEFT_MARGIN, right: RIGHT_MARGIN },
      startY: yOffset,
      styles: {
        fontSize: 8,
        font: "Roboto",
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [59, 78, 135],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "center" }, // no
        1: { halign: "left" }, // Penerima Manfaat
        2: { halign: "right" }, // Rencana Pagi
        3: { halign: "right" }, // Rencana Siang
        4: { halign: "right" }, // Rencana Total
        5: { halign: "right" }, // Pengiriman Pagi
        6: { halign: "right" }, // Pengiriman Siang
        7: { halign: "right" }, // Pengiriman Total
        8: { halign: "right" }, // Diterima Pagi
        9: { halign: "right" }, // Diterima Siang
        10: { halign: "right" }, // Diterima Total
      },
    });

    yOffset = doc.lastAutoTable.finalY + 12;

    // Signature Block
    const signatureX1 = LEFT_MARGIN;

    doc.setFontSize(10);
    doc.setFont("Roboto", "normal");
    doc.setTextColor(0, 0, 0);

    // Pihak Dapur
    doc.text("Kepala SPPG", signatureX1, yOffset);
    doc.line(signatureX1, yOffset + 25, signatureX1 + 60, yOffset + 25);
    // doc.text("Nama:", signatureX1, yOffset + 35);
    // doc.text("Jabatan:", signatureX1, yOffset + 42);
    // doc.text("Jam Terima:", signatureX1, yOffset + 49);

    const addFooter = (doc) => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const footerText = `Halaman ${i} dari ${pageCount}`;
        doc.setFontSize(7);
        doc.text(
          footerText,
          pageWidth - doc.getTextWidth(footerText) - RIGHT_MARGIN,
          pageHeight - 10,
        );
      }
    };

    addFooter(doc);

    doc.save(
      `laporan-produksi-harian-${selectedDate.toISOString().split("T")[0]}.pdf`,
    );
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex flex-row items-center gap-4 mb-4">
        <div
          className="bg-white p-2 rounded-full cursor-pointer"
          onClick={() => navigate("/tabeljadwalcustomer")}
        >
          <BackButton className="rotate-180 text-black" />
        </div>
        <h2 className="text-2xl font-medium">Laporan Harian</h2>
      </div>

      {/* Date Picker */}
      <div className="flex items-center justify-between gap-8 mb-4">
        <div className="flex items-center gap-4">
          <label className="text-gray-700 font-medium">Pilih Tanggal:</label>
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat="yyyy-MM-dd"
            locale="id"
            customInput={<CustomInput value={selectedDate} isOpen={isOpen} />}
            onCalendarOpen={() => setIsOpen(true)}
            onCalendarClose={() => setIsOpen(false)}
          />
        </div>
        <div className="flex flex-row gap-2">
          <Button
            className="bg-oren-no hover:bg-oren-hover"
            onClick={downloadPDF}
            disabled={data.length === 0}
          >
            Download PDF
          </Button>
          {[3, 4, 5].includes(roleId) && (
            <Select
              onValueChange={(value) => navigate(value)}
              value={location.pathname}
            >
              <SelectTrigger className="h-ful">
                <SelectValue placeholder="Pilih Laporan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="/dailyreport">Laporan Harian</SelectItem>
                <SelectItem value="/weeklyreport">Laporan Mingguan</SelectItem>
                <SelectItem value="/monthlyreport">Laporan Bulanan</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg">
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="hidden md:block">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-white font-semibold text-gray-700">
                  <th rowSpan="2" className="px-4 py-2 text-center">
                    No
                  </th>
                  <th rowSpan="2" className="px-4 py-2 text-left">
                    Sekolah
                  </th>
                  <th colSpan="2" className="px-4 py-2 text-center">
                    Jumlah Dijadwalkan
                  </th>
                  <th colSpan="2" className="px-4 py-2 text-center">
                    Jumlah Pengiriman
                  </th>
                  <th colSpan="2" className="px-4 py-2 text-center">
                    Jumlah Diterima
                  </th>
                  <th colSpan="2" className="px-4 py-2 text-center">
                    Rating
                  </th>
                </tr>
                <tr className="border-b bg-white font-semibold text-gray-700">
                  <th className="px-4 py-2 text-center">Pagi</th>
                  <th className="px-4 py-2 text-center">Siang</th>
                  <th className="px-4 py-2 text-center">Pagi</th>
                  <th className="px-4 py-2 text-center">Siang</th>
                  <th className="px-4 py-2 text-center">Pagi</th>
                  <th className="px-4 py-2 text-center">Siang</th>
                  <th className="px-4 py-2 text-center">Kuantitas</th>
                  <th className="px-4 py-2 text-center">Kualitas</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <tr
                      key={item.customer_id}
                      className={`${index % 2 === 0 ? "bg-transparent" : "bg-white"} border-b`}
                    >
                      <td className="px-4 py-2 text-center">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="px-4 py-2 text-left">
                        {item.customer_name || "-"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {item.pagi.qty_sch != null
                          ? formatNumber(item.pagi.qty_sch)
                          : "-"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {item.siang.qty_sch != null
                          ? formatNumber(item.siang.qty_sch)
                          : "-"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {item.pagi.qty_real != null
                          ? formatNumber(item.pagi.qty_real)
                          : "-"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {item.siang.qty_real != null
                          ? formatNumber(item.siang.qty_real)
                          : "-"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {item.pagi.received_qty != null
                          ? formatNumber(item.pagi.received_qty)
                          : "-"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {item.siang.received_qty != null
                          ? formatNumber(item.siang.received_qty)
                          : "-"}
                      </td>
                      <td className="px-4 py-2 text-center tabular-nums">
                        {!isNaN(parseFloat(item.rate_qty))
                          ? parseFloat(item.rate_qty).toFixed(1)
                          : "-"}
                      </td>
                      <td className="px-4 py-2 text-center tabular-nums">
                        {!isNaN(parseFloat(item.rate_quality))
                          ? parseFloat(item.rate_quality).toFixed(1)
                          : "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-gray-500">
                      Tidak ada data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="md:hidden space-y-4">
            {paginatedData.length > 0 ? (
              paginatedData.map((item, index) => (
                <div
                  key={item.customer_id}
                  className="border rounded-xl p-4 shadow-sm bg-white"
                >
                  <p className="text-sm font-semibold mb-2">
                    {(currentPage - 1) * itemsPerPage + index + 1}.{" "}
                    {item.customer_name}
                  </p>
                  <p className="text-xs text-gray-600 mb-1">
                    Tanggal:{" "}
                    {new Date(item.date).toLocaleDateString("id-ID", {
                      year: "numeric",
                      month: "long",
                      day: "2-digit",
                    })}
                  </p>
                  <div className="text-xs space-y-1">
                    <p className="font-semibold">PAGI</p>
                    <p>
                      Jumlah Dijadwalkan:{" "}
                      <span className="float-right">
                        {item.pagi.qty_sch ?? "-"}
                      </span>
                    </p>
                    <p>
                      Jumlah Pengiriman:{" "}
                      <span className="float-right">
                        {item.pagi.qty_real ?? "-"}
                      </span>
                    </p>
                    <p>
                      Jumlah Diterima:{" "}
                      <span className="float-right">
                        {item.pagi.received_qty ?? "-"}
                      </span>
                    </p>
                  </div>
                  <div className="text-xs space-y-1 mt-2">
                    <p className="font-semibold">SIANG</p>
                    <p>
                      Jumlah Dijadwalkan:{" "}
                      <span className="float-right">
                        {item.siang.qty_sch ?? "-"}
                      </span>
                    </p>
                    <p>
                      Jumlah Pengiriman:{" "}
                      <span className="float-right">
                        {item.siang.qty_real ?? "-"}
                      </span>
                    </p>
                    <p>
                      Jumlah Diterima:{" "}
                      <span className="float-right">
                        {item.siang.received_qty ?? "-"}
                      </span>
                    </p>
                  </div>
                  <div className="text-xs space-y-1 mt-2">
                    <p className="font-semibold">Rating</p>
                    <p>
                      Rating Kuantitas:{" "}
                      <span className="float-right">
                        {!isNaN(parseFloat(item.rate_qty))
                          ? parseFloat(item.rate_qty).toFixed(1)
                          : "-"}
                      </span>
                    </p>
                    <p>
                      Rating Kualitas:{" "}
                      <span className="float-right">
                        {!isNaN(parseFloat(item.rate_quality))
                          ? parseFloat(item.rate_quality).toFixed(1)
                          : "-"}
                      </span>
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-gray-600">
                Tidak ada data.
              </p>
            )}
          </div>
        )}
        {/* Pagination Controls */}
        <div className="flex justify-center items-center mt-4 text-xs sm:text-sm">
          {/* Previous Button */}
          <button
            className={`px-2 py-2 rounded ${
              currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-500 hover:text-black"
            }`}
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            <span className="font-bold text-lg sm:text-2xl">{"<"}</span>
          </button>

          {/* Page Numbers */}
          <div className="flex items-center">
            {pageNumbers.map((page, index) => (
              <span
                key={index}
                onClick={() => typeof page === "number" && setCurrentPage(page)}
                className={`mx-1 px-2 sm:px-3 py-1 cursor-pointer rounded-full ${
                  currentPage === page
                    ? "text-black font-semibold"
                    : "text-gray-700"
                } ${page === "..." ? "cursor-default text-gray-400" : ""}`}
              >
                {page}
              </span>
            ))}
          </div>

          {/* Next Button */}
          <button
            className={`px-2 py-2 rounded ${
              currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-500 hover:text-black"
            }`}
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            <span className="font-bold text-lg sm:text-2xl">{">"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
